/**
 * @author Charles Carlson
 * @copyright Copyright 2026 by Radivision Inc., CA, USA. All Rights Reserved.
 * @Date: 2026-08-18
 * @description Implementation of gtag abstract class and concrete class for gtag events.
 * @filename gtag-analytics.ts
 */
import { consentStore } from "./gtag-consent-store";
import { EVENT_NAMES } from "../constants/gtag-event-names";
declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

abstract class GtagEvent {
  abstract readonly name: string;
  abstract getParams(): Record<string, string | number | boolean>;
  send(): void {
    if (consentStore.get("analytics_storage") === "denied") {
      return;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: this.name, ...this.getParams() });
  }
}

class RegistrationEvent extends GtagEvent {
  readonly name: string = EVENT_NAMES.REGISTRATION_EVENT;

  constructor(private method: string) {
    super();
  }
  getParams(): Record<string, string | number | boolean> {
    return { method: this.method };
  }
}
class LoginEvent extends GtagEvent {
  readonly name: string = EVENT_NAMES.LOGIN_EVENT;
  constructor(private method: string) {
    super();
  }
  getParams(): Record<string, string | number | boolean> {
    return { method: this.method };
  }
}

class LogoutEvent extends GtagEvent {
  readonly name: string = EVENT_NAMES.LOGOUT_EVENT;
  constructor(private method: string) {
    super();
  }
  getParams(): Record<string, string | number | boolean> {
    return { method: this.method };
  }
}

class PageViewEvent extends GtagEvent {
  readonly name = EVENT_NAMES.PAGE_VIEW_EVENT;

  constructor(private pagePath: string, private pageTitle: string) {
    super();
  }

  getParams() {
    return {
      page_path: this.pagePath,
      page_title: this.pageTitle,
    };
  }
}

class ContentClickEvent extends GtagEvent {
  readonly name = EVENT_NAMES.CONTENT_CLICK_EVENT;

  constructor(private contentCategory: string, private contentTitle: string) {
    super();
  }
  getParams(): Record<string, string | number | boolean> {
    return {
      content_category: this.contentCategory,
      content_title: this.contentTitle,
    };
  }
}
class SupportEvent extends GtagEvent {
  readonly name = EVENT_NAMES.SUPPORT_EVENT;

  constructor(private channel: string, private outcome: string) {
    super();
  }

  getParams(): Record<string, string | number | boolean> {
    return {
      channel: this.channel,
      outcome: this.outcome,
    };
  }
}

class PersonInfoClickEvent extends GtagEvent {
  readonly name = EVENT_NAMES.PERSON_INFO_CLICK_EVENT;
  constructor(private personName: string) {
    super();
  }
  getParams(): Record<string, string | number | boolean> {
    return { person_name: this.personName };
  }
}

class VideoProgressEvent extends GtagEvent {
  readonly name = EVENT_NAMES.VIDEO_PROGRESS_EVENT;
  constructor(
    private videoTitle: string,
    private percentPlayed: number,
    private elapsed_sec: number,
    private duration: number,
    private event_type: string,
  ) {
    super();

    //handling for unnaterual video lengths or percentages, stage as 0, but still usable as user is watching.
    if (percentPlayed < 0 || percentPlayed > 100) {
      this.percentPlayed = 0;
    }
  }
  getParams(): Record<string, string | number | boolean> {
    return {
      video_title: this.videoTitle,
      percent_played: this.percentPlayed,
      elapsed_sec: this.elapsed_sec,
      duration: this.duration,
      event_type: this.event_type,
    };
  }
}

class SavedItemEvent extends GtagEvent {
  readonly name = EVENT_NAMES.SAVED_ITEM_EVENT;
  constructor(private itemId: string, private action: string, private itemType: string) {
    super();
  }
  getParams(): Record<string, string | number | boolean> {
    return {
      item_id: this.itemId,
      action: this.action,
      item_type: this.itemType,
    };
  }
}

class RSVPEvent extends GtagEvent {
  readonly name = EVENT_NAMES.RSVP_EVENT;
  constructor(private eventId: string) {
    super();
  }
  getParams(): Record<string, string | number | boolean> {
    return { event_id: this.eventId };
  }
}

export {
  RegistrationEvent,
  LoginEvent,
  LogoutEvent,
  PageViewEvent,
  ContentClickEvent,
  SupportEvent,
  PersonInfoClickEvent,
  VideoProgressEvent,
  SavedItemEvent,
  RSVPEvent,
  GtagEvent,
};
